/**
 * render.ts
 * ---------------------------------------------------------------------------
 * ЕДИНЫЙ источник правды для config → видимый HTML компонента (бывш.
 * ep-research/assets/js/components-render.js, теперь TS).
 *
 * Используется ДВУМЯ потребителями:
 *   • Тема (browser): components/browser.ts рендерит плейсхолдеры на клиенте
 *     как fallback / превью.
 *   • Origin (Node-прокси): transform/render-components.ts разворачивает
 *     <div data-component> в семантический HTML server-side (это видят no-JS
 *     краулеры).
 *
 * Контракт рендерера:  fn(config, opts) → HTML-строка
 *   opts.inlineLd — если true, в блок встраивается <script type=ld+json> через
 *                   buildEntity (тот же источник, что и @graph). Прокси зовёт с
 *                   inlineLd:false — схема идёт одним @graph в <head>.
 *   opts.siteUrl / opts.pageUrl — нужны только для inline LD.
 * ---------------------------------------------------------------------------
 */

import { buildEntity } from './core';
import type {
    ComponentType, RenderOptions,
    BlufConfig, MethodologyConfig, BenchmarkTableConfig, RadarChartConfig,
    ModelCardConfig, DatasetDownloadConfig, FaqConfig, ChangelogConfig, FindingConfig,
} from './types';

type Renderer = (config: any, opts: RenderOptions) => string;

const SERIES_COLORS = ['#7000ff', '#ff6b35', '#00c2a8', '#ffb800', '#ff4d6d', '#3a86ff'];

const DOWNLOAD_ICON = '<svg class="data-download__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">'
    + '<path d="M12 3v12m0 0-4-4m4 4 4-4M5 20h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
    + '</svg>';

/* ── helpers ──────────────────────────────────────────────────────────── */

function esc(s: unknown): string {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => {
        const map: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
        return map[c]!;
    });
}

function jsonLd(data: unknown): string {
    // intentional split to avoid the closing tag breaking out of the surrounding string
    return '<script type="application/ld+json">'
        + JSON.stringify(data).replace(/</g, '\\u003c')
        + '<\/script>';
}

/* 1-based номер компонента среди себе подобных. Приходит извне (bundler /
   browser / markdown-mirror), потому что рендерер видит только свой config и
   позицию в документе знать не может. Мусор и отсутствие → 1. */
function ordinal(opts: RenderOptions): number {
    const n = Math.trunc(Number(opts?.ordinal));
    return Number.isFinite(n) && n > 0 ? n : 1;
}

/* Inline JSON-LD из общего ядра (core.ts). Прокси зовёт с inlineLd:false →
   buildEntity вообще не дёргается. */
function ldFor(type: ComponentType, c: Record<string, any>, opts: RenderOptions): string {
    if (!opts || !opts.inlineLd) return '';
    const ent = buildEntity(type, c, { siteUrl: opts.siteUrl, pageUrl: opts.pageUrl });
    if (!ent) return '';
    const withCtx: Record<string, unknown> = { '@context': 'https://schema.org' };
    for (const k in ent) {
        if (Object.prototype.hasOwnProperty.call(ent, k)) {
            withCtx[k] = (ent as Record<string, unknown>)[k];
        }
    }
    return jsonLd(withCtx);
}

/* ── 1. BLUF Block ────────────────────────────────────────────────── */

/* BLUF: видимый HTML-элемент без микроразметки. Его текст несёт Article.abstract
   в @graph (RES-20) — отдельной WebPageElement-сущности больше нет. */
function bluf(c: BlufConfig): string {
    return ''
        + '<section class="bluf" data-component="bluf">'
        +     '<p class="bluf__text">' + esc(c.text) + '</p>'
        + '</section>';
}

/* ── 2. Methodology Box ───────────────────────────────────────────── */

function methodologyBox(c: MethodologyConfig, opts: RenderOptions): string {
    const models = (c.models || []).map((m) => {
        return '<li class="methodology-box__model">' + esc(m) + '</li>';
    }).join('');
    const seeds = (c.seeds || []).join(', ');

    return ''
        + '<aside class="methodology-box" data-component="methodology-box">'
        +     ldFor('methodology-box', c, opts)
        +     '<h2 class="methodology-box__title">' + esc(c.title || 'Methodology') + '</h2>'
        +     '<dl class="methodology-box__meta">'
        +         (c.prompts_count != null ? '<dt>Prompts</dt><dd>' + esc(c.prompts_count) + '</dd>' : '')
        +         (seeds ? '<dt>Seeds</dt><dd>' + esc(seeds) + '</dd>' : '')
        +         (c.dates ? '<dt>Dates</dt><dd>' + esc(c.dates) + '</dd>' : '')
        +         (c.reviewer ? '<dt>Reviewer</dt><dd>' + esc(c.reviewer) + '</dd>' : '')
        +     '</dl>'
        +     (models ? '<ul class="methodology-box__models">' + models + '</ul>' : '')
        + '</aside>';
}

/* ── 3. Benchmark Table ───────────────────────────────────────────── */

function benchmarkTable(c: BenchmarkTableConfig, opts: RenderOptions): string {
    const columns = c.columns || [];
    const rows = c.rows || [];

    const thead = columns.map((col, i) => {
        return '<th scope="col" class="benchmark-table__header" data-col="' + i + '">'
            + esc(col)
            + '<span class="benchmark-table__sort" aria-hidden="true"></span>'
            + '</th>';
    }).join('');

    const tbody = rows.map((row) => {
        return '<tr class="benchmark-table__row">' + row.map((cell) => {
            return '<td class="benchmark-table__cell">' + esc(cell) + '</td>';
        }).join('') + '</tr>';
    }).join('');

    return ''
        + '<section class="benchmark-table" data-component="benchmark-table" data-sortable="' + (c.sortable !== false) + '">'
        +     ldFor('benchmark-table', c, opts)
        +     '<table class="benchmark-table__table">'
        +         (c.caption ? '<caption class="benchmark-table__caption">' + esc(c.caption) + '</caption>' : '')
        +         '<thead><tr>' + thead + '</tr></thead>'
        +         '<tbody>' + tbody + '</tbody>'
        +     '</table>'
        +     (c.source_link
            ? '<p class="benchmark-table__source">Source: <a href="' + esc(c.source_link) + '">' + esc(c.source_link) + '</a></p>'
            : '')
        + '</section>';
}

/* ── 4. Radar Chart (inline SVG, no deps) ─────────────────────────── */

function radarChart(c: RadarChartConfig): string {
    const dims = c.dimensions || [];
    const models = c.models || [];
    const scores = c.scores || [];
    const n = dims.length;

    if (n < 3 || models.length === 0) {
        return '<div class="radar-chart radar-chart--error" data-component="radar-chart">Radar Chart: needs ≥3 dimensions and ≥1 model</div>';
    }

    const size = 520;
    const cx = size / 2;
    const cy = size / 2;
    const r = size * 0.36;

    function ang(i: number): number { return -Math.PI / 2 + (2 * Math.PI * i) / n; }
    function pt(i: number, scale: number): [number, number] {
        return [
            +(cx + Math.cos(ang(i)) * r * scale).toFixed(2),
            +(cy + Math.sin(ang(i)) * r * scale).toFixed(2),
        ];
    }

    let rings = '';
    for (let k = 1; k <= 5; k++) {
        const ringPts = dims.map((_, i) => pt(i, k / 5).join(',')).join(' ');
        rings += '<polygon points="' + ringPts + '" class="radar-chart__ring"/>';
    }

    const axes = dims.map((_, i) => {
        const p = pt(i, 1);
        return '<line x1="' + cx + '" y1="' + cy + '" x2="' + p[0] + '" y2="' + p[1] + '" class="radar-chart__axis"/>';
    }).join('');

    const labels = dims.map((d, i) => {
        const p = pt(i, 1.15);
        let anchor = 'middle';
        if (p[0] > cx + 8) anchor = 'start';
        else if (p[0] < cx - 8) anchor = 'end';
        return '<text x="' + p[0] + '" y="' + p[1] + '" class="radar-chart__label" text-anchor="' + anchor + '" dominant-baseline="middle">'
            + esc(d) + '</text>';
    }).join('');

    const series = models.map((_, mi) => {
        const color = SERIES_COLORS[mi % SERIES_COLORS.length];
        const seriesPts = dims.map((_, di) => {
            const v = ((scores[mi] || [])[di] || 0);
            return pt(di, Math.max(0, Math.min(1, v))).join(',');
        }).join(' ');
        return '<polygon points="' + seriesPts + '" class="radar-chart__series" style="--c:' + color + '"/>';
    }).join('');

    const legend = models.map((m, mi) => {
        const color = SERIES_COLORS[mi % SERIES_COLORS.length];
        return '<li class="radar-chart__legend-item">'
            + '<span class="radar-chart__swatch" style="background:' + color + '"></span>'
            + '<span class="radar-chart__legend-name">' + esc(m) + '</span>'
            + '</li>';
    }).join('');

    return ''
        + '<figure class="radar-chart" data-component="radar-chart">'
        +     '<svg viewBox="0 0 ' + size + ' ' + size + '" class="radar-chart__svg" role="img" aria-label="' + esc(c.title || 'Radar chart') + '">'
        +         rings + axes + series + labels
        +     '</svg>'
        +     '<ul class="radar-chart__legend">' + legend + '</ul>'
        +     (c.caption ? '<figcaption class="radar-chart__caption">' + esc(c.caption) + '</figcaption>' : '')
        + '</figure>';
}

/* ── 5. Model Card ────────────────────────────────────────────────── */

function modelCard(c: ModelCardConfig, opts: RenderOptions): string {
    function row(label: string, value: unknown): string {
        return value ? '<dt>' + esc(label) + '</dt><dd>' + esc(value) + '</dd>' : '';
    }

    const name = c.url
        ? '<a href="' + esc(c.url) + '">' + esc(c.name) + '</a>'
        : '<span>' + esc(c.name) + '</span>';

    return ''
        + '<article class="model-card" data-component="model-card">'
        +     ldFor('model-card', c, opts)
        +     '<header class="model-card__head">'
        +         '<h3 class="model-card__name">' + name + '</h3>'
        +         (c.provider ? '<p class="model-card__provider">' + esc(c.provider) + '</p>' : '')
        +     '</header>'
        +     '<dl class="model-card__meta">'
        +         row('License', c.license)
        +         row('Best for', c.best_for)
        +         row('Fails on', c.fails_on)
        +         row('Cost', c.cost)
        +         (c.hit_rate != null ? '<dt>Hit rate</dt><dd class="model-card__rate">' + esc(c.hit_rate) + '</dd>' : '')
        +     '</dl>'
        + '</article>';
}

/* ── 6. Prompt Pack Download ──────────────────────────────────────── */

function promptPack(c: DatasetDownloadConfig, opts: RenderOptions): string {
    const meta = [
        esc(c.format || 'CSV'),
        c.count ? esc(c.count) + ' prompts' : '',
        c.license ? esc(c.license) : '',
    ].filter(Boolean).join(' / ');

    return ''
        + '<aside class="data-download data-download--prompts" data-component="prompt-pack">'
        +     ldFor('prompt-pack', c, opts)
        +     '<p class="data-download__label">Prompt Pack</p>'
        +     '<a class="data-download__btn" href="' + esc(c.file_url) + '" download>'
        +         DOWNLOAD_ICON
        +         '<span class="data-download__btn-text">' + esc(c.title || 'Prompt pack') + '</span>'
        +     '</a>'
        +     '<p class="data-download__meta">' + meta + '</p>'
        + '</aside>';
}

/* ── 7. FAQ Block ─────────────────────────────────────────────────── */
/* Ревизия 02.06: видимый блок БЕЗ FAQPage-разметки. Google отключил FAQ rich
   results 07.05.2026, а ИИ-движки разметку не цитируют — ценность только в
   видимом тексте с правильной семантикой (<details>/<summary>). Поэтому ни
   JSON-LD, ни микроданных Question/Answer здесь нет (opts не используется). */
function faqBlock(c: FaqConfig): string {
    const items = c.questions || [];

    const html = items.map((qa, i) => {
        return ''
            + '<details class="faq-block__item"' + (i === 0 ? ' open' : '') + '>'
            +     '<summary class="faq-block__question">' + esc(qa.q) + '</summary>'
            +     '<div class="faq-block__answer"><p>' + esc(qa.a) + '</p></div>'
            + '</details>';
    }).join('');

    return ''
        + '<section class="faq-block" data-component="faq">'
        +     (c.title ? '<h2 class="faq-block__title">' + esc(c.title) + '</h2>' : '')
        +     html
        + '</section>';
}

/* ── 8. CSV / JSON Download (raw data) ────────────────────────────── */

function dataDownload(c: DatasetDownloadConfig, opts: RenderOptions): string {
    const meta = [
        esc(c.format || 'CSV'),
        c.license ? esc(c.license) : '',
        c.schema_link ? '<a href="' + esc(c.schema_link) + '">schema</a>' : '',
    ].filter(Boolean).join(' / ');

    return ''
        + '<aside class="data-download" data-component="data-download">'
        +     ldFor('data-download', c, opts)
        +     '<p class="data-download__label">Raw data</p>'
        +     '<a class="data-download__btn" href="' + esc(c.file_url) + '" download>'
        +         DOWNLOAD_ICON
        +         '<span class="data-download__btn-text">' + esc(c.title || 'Raw test data') + '</span>'
        +     '</a>'
        +     '<p class="data-download__meta">' + meta + '</p>'
        + '</aside>';
}

/* ── 9. Changelog (page-level, RES-21) ────────────────────────────── */
/* Видимый аккордеон истории правок внизу страницы. Без schema.org: ни
   `reviewedBy`, ни кастомного `Article.changelog` нет в словаре — невалидные
   свойства не эмитим (ревизия 02.06). Ценность — в видимом тексте. */
function changelog(c: ChangelogConfig): string {
    const entries = c.entries || [];
    if (entries.length === 0) return '';

    const items = entries.map((e) => {
        return ''
            + '<li class="changelog__item">'
            +     '<time class="changelog__date" datetime="' + esc(e.date) + '">' + esc(e.date) + '</time>'
            +     '<span class="changelog__change">' + esc(e.change) + '</span>'
            + '</li>';
    }).join('');

    const n = entries.length;
    return ''
        + '<section class="changelog" data-component="changelog">'
        +     '<details class="changelog__details">'
        +         '<summary class="changelog__summary">'
        +             '<span class="changelog__title">' + esc(c.title || 'Changelog') + '</span>'
        +             '<span class="changelog__count">' + n + (n === 1 ? ' update' : ' updates') + '</span>'
        +         '</summary>'
        +         '<ol class="changelog__list">' + items + '</ol>'
        +     '</details>'
        + '</section>';
}

/* ── 10. Finding ──────────────────────────────────────────────────── */

/* Ключевой вывод исследования: текстовый блок на плашке с якорем #finding-N.
   Якорь — весь смысл компонента: на него ссылаются снаружи и его цитируют
   ИИ-движки, поэтому номер НЕ берётся из конфига. Его считает вызывающий
   (opts.ordinal) по позиции в документе — иначе редактор, вставив finding в
   середину статьи, рассинхронит подпись, id и все внешние ссылки.
   Подпись по умолчанию «Finding N» — та же N, что в id, разъехаться не могут.
   Без schema.org сущности, как BLUF: ценность в видимом тексте (ревизия 02.06). */
function finding(c: FindingConfig, opts: RenderOptions): string {
    const text = esc(c.text);
    if (!text) return '';   // пустой блок не рисуем — плашка без текста бессмысленна

    const n = ordinal(opts);
    return ''
        + '<section class="finding" id="finding-' + n + '" data-component="finding">'
        +     '<p class="finding__label">' + (c.title ? esc(c.title) : 'Finding ' + n) + '</p>'
        +     '<p class="finding__text">' + text + '</p>'
        + '</section>';
}

/* ── registry ─────────────────────────────────────────────────────── */

export const RENDERERS: Partial<Record<ComponentType, Renderer>> = {
    'bluf': bluf,
    'methodology-box': methodologyBox,
    'benchmark-table': benchmarkTable,
    'radar-chart': radarChart,
    'model-card': modelCard,
    'prompt-pack': promptPack,
    'faq': faqBlock,
    'data-download': dataDownload,
    'changelog': changelog,
    'finding': finding,
};

export function renderComponent(type: string, config: Record<string, any>, opts: RenderOptions = {}): string {
    const fn = RENDERERS[type as ComponentType];
    if (!fn || !config) return '';
    return fn(config, opts);
}
